import React from 'react';
import { User, Phone, MapPin, MessageSquare, Eye, Edit, Power, Star, ShieldCheck } from 'lucide-react';
import Avatar from '../../../../components/common/Avatar';

const CustomerCard = ({
    customer,
    onView,
    onEdit,
    onToggleStatus,
    isSalesPerson = false,
    selected = false,
    onToggleSelect = null
}) => {
    // Generate a subtle background color based on category
    const getCategoryStyles = (category) => {
        const cat = (category || '').toLowerCase();
        if (cat.includes('gold') || cat.includes('premium')) return 'bg-amber-50 text-amber-700 border-amber-100';
        if (cat.includes('silver') || cat.includes('vip')) return 'bg-slate-50 text-slate-700 border-slate-100';
        return 'bg-blue-50 text-blue-700 border-blue-100';
    };

    return (
        <div 
            className={`group bg-white rounded-2xl border transition-all duration-300 overflow-hidden flex flex-col relative ${
                selected
                ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md'
                : !customer.active 
                    ? 'border-gray-200 opacity-75 grayscale-[0.3]' 
                    : 'border-gray-100 hover:border-blue-200 shadow-sm hover:shadow-md'
            }`}
        >
            {/* Header / Avatar Section */}
            <div className="p-4 flex items-start gap-4 relative">
                {onToggleSelect && (
                    <div className="absolute top-3 right-3 z-10">
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={onToggleSelect}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer shadow-sm"
                        />
                    </div>
                )}
                <div className="relative">
                    <Avatar name={customer.name} size="lg" shape="squircle" className="shadow-sm ring-2 ring-white" />
                    {customer.active && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full shadow-sm" />
                    )}
                </div>
                
                <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <h3 className="font-bold text-gray-900 text-base leading-tight truncate group-hover:text-blue-600 transition-colors">
                            {customer.name}
                        </h3>
                    </div>
                    
                    {customer.categoryName && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${getCategoryStyles(customer.categoryName)}`}>
                            <Star size={10} fill="currentColor" className="opacity-70" />
                            {customer.categoryName}
                        </span>
                    )}
                    
                    {!customer.active && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-gray-100 text-gray-500 border border-gray-200">
                            Inactive
                        </span>
                    )}
                </div>
            </div>

            {/* Content Details */}
            <div className="px-4 pb-4 flex-1 space-y-3">
                {/* Contact Person */}
                <div className="bg-gray-50/50 rounded-xl p-3 border border-gray-50 group-hover:bg-blue-50/30 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                        <User size={12} className="text-gray-400" />
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Contact Person</span>
                    </div>
                    <p className="text-sm font-bold text-gray-800 px-1">
                        {customer.contactPerson || '—'}
                        {customer.designation && (
                            <span className="text-[11px] font-medium text-gray-500 ml-2 italic">({customer.designation})</span>
                        )}
                    </p>
                </div>

                {/* Sales Persons */}
                <div className="flex flex-col gap-2 pl-1">
                    <div className="flex items-center gap-2.5 text-xs">
                        <div className="w-5 h-5 rounded bg-blue-50 flex items-center justify-center shrink-0">
                            <ShieldCheck size={12} className="text-blue-500" />
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight mr-1">Assigned:</span>
                            {customer.assignedSalesPersons?.length > 0 ? (
                                customer.assignedSalesPersons.map((sp, idx) => (
                                    <span key={idx} className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">
                                        {sp.fullName}
                                    </span>
                                ))
                            ) : (
                                <span className="text-[10px] text-gray-400 font-medium">—</span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2.5 text-xs">
                        <div className="w-5 h-5 rounded bg-emerald-50 flex items-center justify-center shrink-0">
                            <MessageSquare size={12} className="text-emerald-500" />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-gray-700">{customer.whatsappNumber || customer.mobileNumber || '—'}</span>
                            {customer.whatsappNumber && (
                                <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded uppercase tracking-tighter">WhatsApp</span>
                            )}
                        </div>
                    </div>

                    <div className="flex items-start gap-2.5 text-xs">
                        <div className="w-5 h-5 rounded bg-orange-50 flex items-center justify-center shrink-0 mt-0.5">
                            <MapPin size={12} className="text-orange-500" />
                        </div>
                        <span className="text-gray-600 leading-relaxed line-clamp-2">
                            {customer.address || 'No address provided'}
                            {customer.stateName && <span className="block font-semibold text-gray-500 mt-0.5">{customer.stateName}, {customer.countryName}</span>}
                        </span>
                    </div>
                </div>
            </div>

            {/* Action Bar */}
            <div className="px-4 py-3 bg-gray-50/80 border-t border-gray-100 flex items-center justify-between mt-auto">
                <button 
                    onClick={() => onView(customer)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 text-[11px] font-bold text-blue-600 hover:bg-blue-100/50 rounded-xl transition-all"
                >
                    <Eye size={14} /> View Details
                </button>
                
                <div className="flex items-center gap-1 ml-2">
                    <button 
                        onClick={() => onEdit(customer)}
                        className="p-2 text-orange-600 hover:bg-orange-100/50 rounded-xl transition-all"
                        title="Edit Customer"
                    >
                        <Edit size={14} />
                    </button>
                    {!isSalesPerson && (
                        <button 
                            onClick={() => onToggleStatus(customer)}
                            className={`p-2 rounded-xl transition-all ${
                                customer.active 
                                ? 'text-red-600 hover:bg-red-100/50' 
                                : 'text-green-600 hover:bg-green-100/50'
                            }`}
                            title={customer.active ? 'Deactivate' : 'Activate'}
                        >
                            <Power size={14} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerCard;
